import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { captureException } from '@sentry/nextjs';
import { NEW_LESSON_PAGE } from 'constants/pages';
import { getCoachFollowUrl } from 'constants/user';
import { useGetCoachDashboardStatsLazyQuery } from 'types/generated/client';
import { toTitleCase } from 'utils/shared/toTitleCase';
import { useGetCurrentUser } from 'hooks/useGetCurrentUser';
import { useModal } from 'hooks/useModal';
import { useViewer } from 'hooks/useViewer';
import PeopleColored from 'svg/PeopleColored';
import PlusPlain from 'svg/PlusPlain';
import QRCode from 'svg/QRCode';
import { StarFilled } from 'svg/StarFilled';
import Button, { ButtonLink } from 'components/Button';
import ModalShareProfile from 'components/modals/ModalShareProfile';
import { CoachDashboardHeaderSkeleton } from '../Skeletons';

const CoachDashboardHeader = () => {
  const viewer = useViewer();
  const { user } = useGetCurrentUser();
  const {
    openModal: openShareProfileModal,
    closeModal: closeShareProfileModal,
    isOpen: isShareProfileModalOpen,
  } = useModal();

  const [getCoachDashboardStats, { loading, data }] = useGetCoachDashboardStatsLazyQuery();

  const fetchCoachStats = useCallback(async () => {
    try {
      await getCoachDashboardStats({
        variables: { userId: viewer.userId },
        fetchPolicy: 'cache-and-network',
      });
    } catch (err) {
      captureException(err);
    }
  }, [viewer.userId, getCoachDashboardStats]);

  useEffect(() => {
    if (viewer.userId) {
      fetchCoachStats();
    }
  }, [viewer.userId, fetchCoachStats]);

  const coachProfile = data?.userProfiles?.[0];
  const rating = coachProfile?.coachReviewsAggregate?.aggregate?.avg?.rating;
  const followerCount = coachProfile?.followersAggregate?.aggregate?.count || 0;
  const coachName = coachProfile?.preferredName || coachProfile?.fullName || 'Coach';
  const shareUrl =
    typeof window !== 'undefined'
      ? getCoachFollowUrl(user?.username?.toLowerCase() || user?.id || '')
      : '';

  if (loading) {
    return <CoachDashboardHeaderSkeleton />;
  }

  return (
    <>
      <div className="border-b border-color-border-input-lightmode py-ds-3xl dark:border-color-border-input-darkmode max-sm:py-5">
        <div className="container mx-auto flex flex-wrap gap-ds-xl  max-sm:px-4">
          <h3 className="typography-product-heading-desktop max-md:typography-product-heading-mobile">
            Welcome, Coach {`${toTitleCase(coachName)}!`}
          </h3>

          <div className="flex flex-1 justify-between gap-ds-md">
            <div className="flex">
              <div className="flex items-center gap-1.5 border-l border-color-border-input-lightmode  px-ds-lg py-1.5 dark:border-color-border-input-darkmode max-sm:flex-col max-sm:justify-between sm:py-2">
                <StarFilled className="mb-0.5 h-4 w-4" />
                <span className="typography-product-subheading max-sm:typography-product-card-title-mobile text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                  {rating ? rating.toFixed(1) : 'N/A'}
                </span>
              </div>
              <div className="flex items-center gap-1.5 border-l border-r border-color-border-input-lightmode px-ds-lg py-1.5 dark:border-color-border-input-darkmode max-sm:flex-col max-sm:justify-between sm:py-2">
                <PeopleColored className="h-4 w-5" />
                <span className="typography-product-subheading max-sm:typography-product-card-title-mobile text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                  {followerCount}
                </span>
              </div>
            </div>
            <div className="flex gap-ds-md max-md:items-center">
              <ButtonLink
                variant="brand"
                sizeMobile="sm"
                sizeDesktop="md"
                iconLeft={<PlusPlain className="h-3.5 w-3.5 md:h-5 md:w-5" />}
                className="w-fit whitespace-nowrap"
                href={NEW_LESSON_PAGE}
              >
                Create Lesson
              </ButtonLink>
              <Button
                variant="secondary"
                size="md"
                iconLeft={<QRCode className="h-5 w-5 " />}
                className="w-fit whitespace-nowrap max-md:hidden"
                onClick={() => openShareProfileModal()}
              >
                Share Profile
              </Button>
              <Button
                variant="secondary"
                size="icon"
                iconLeft={<QRCode className="h-3.5 w-3.5 md:h-5 md:w-5" viewBox="0 0 16 16" />}
                className="h-[2rem] w-[2rem] shrink-0  md:hidden"
                onClick={() => openShareProfileModal()}
              />
            </div>
          </div>
        </div>
      </div>
      <ModalShareProfile
        isOpen={isShareProfileModalOpen}
        closeModal={closeShareProfileModal}
        shareUrl={shareUrl}
        title="Share Profile"
      />
    </>
  );
};

export default CoachDashboardHeader;
